<template>
  <div style="overflow: hidden;">
    <LineChart 
      v-if="hrChartUnit === hrUnits.PERCENTAGE"
      :key="hrUnits.PERCENTAGE"
      :data="generateChartData()"
      :duration="duration"
      :height="500"
      :x="{ min: 0, max: duration}"
      :y="vAxisMaxMinValues"
      :x-unit="'min'"
      :y-unit="'%'"
      :y-axis-title="vAxisTitle"
      :x-axis-title="$t('CALENDAR_ACTIVITY.activityCharts.hrActivityChart.xAxisTitle')"
      :y-ticks="yTicks"
      :x-max-seconds="activityInfo.durationInSeconds"
      :display-brushes="activityType === sessionTypes.INDIVIDUAL && activityInfo.status === 'PROCESSED'"
      :on-brush-creation="onBrushCreation"
      :segments="filteredSegments"
      :activity-status="activityInfo.status"
      :trigger-more-details="getMorePreciseHeartRateData"
      :chart-data-medium-precise="mediumPreciseChartDataHrPercentage"
      :chart-data-high-precise="highPreciseChartDataHrPercentage"
      :chart-data-highest-precise="highestPreciseChartDataHrPercentage"
      :player-info="activityInfo"
      :main-line-color="'#F68D2E'"
      :chartMetricsType="'HR_PERCENTAGE'"
      :zoomSelection="zoomSelection"
      :updateZoomSelection="onUpdateSelection"
    />

    <LineChart 
      :key="hrUnits.BPM"
      v-if="hrChartUnit === hrUnits.BPM"
      :data="generateChartData()"
      :duration="duration"
      :height="500"
      :x="{ min: 0, max: duration}"
      :y="vAxisMaxMinValues"
      :x-unit="'min'"
      :y-unit="'BPM'"
      :y-axis-title="vAxisTitle"
      :x-axis-title="$t('CALENDAR_ACTIVITY.activityCharts.hrActivityChart.xAxisTitle')"
      :y-ticks="yTicks"
      :x-max-seconds="activityInfo.durationInSeconds"
      :display-brushes="activityType === sessionTypes.INDIVIDUAL && activityInfo.status === activityStatus.PROCESSED"
      :on-brush-creation="onBrushCreation"
      :segments="filteredSegments"
      :activity-status="activityInfo.status"
      :trigger-more-details="getMorePreciseHeartRateData"
      :chart-data-medium-precise="mediumPreciseChartDataHrBPM"
      :chart-data-high-precise="highPreciseChartDataHrBPM"
      :chart-data-highest-precise="highestPreciseChartDataHrBPM"
      :player-info="activityInfo"
      :main-line-color="'#F68D2E'"
      :chartMetricsType="'HR_BPM'"
      :zoomSelection="zoomSelection" 
      :updateZoomSelection="onUpdateSelection" />

  </div>
</template>

<script>
import { ACTIVITY_STATUS } from '@/constants/SessionConsts';
import { SESSION_TYPES } from '@/constants/SessionConsts';
import ChartMixin from '@/mixins/ChartMixin'
import LineChart from '@/components/common/chart/LineChart'
import { TIME_ABOVE_80_PERCENT_HR_UNITS } from '@/constants/SessionConsts'

export default {
  name: 'HRActivityChart',
  props: [
    'metric',
    'duration',
    'vAxixMaxMinValues',
    'hrChartUnit',
    'generateBpmChartData',
    'generatePercentageChartData',
    'segments',
    'filteredSegments',
    'createActivitySegment',
    'deleteSegment',
    'activityId',
    'brushCreatingTriggered',
    'activityType',
    'activityInfo',
    'fetchMorePreciseActivityData',
    'mediumPreciseChartDataHrPercentage',
    'mediumPreciseChartDataHrBPM',
    'highPreciseChartDataHrBPM',
    'highPreciseChartDataHrPercentage',
    'highestPreciseChartDataHrBPM',
    'highestPreciseChartDataHrPercentage',
    'zoomSelection',
    'onUpdateSelection'
  ],
  data () {
    return {
      hrUnits: TIME_ABOVE_80_PERCENT_HR_UNITS,
      showModal: false,
      selection: null,
      sessionTypes: SESSION_TYPES,
      activityStatus: ACTIVITY_STATUS
    }
  },
  computed: {
    generateChartData () {
      return this.hrChartUnit === this.hrUnits.BPM ? this.generateBpmChartData : this.generatePercentageChartData
    },
    vAxisMaxMinValues () {
      return this.hrChartUnit === this.hrUnits.BPM ? 
        { max: 260, min: 40} : 
        { max: 110, min: 30}
    },
    vAxisTitle () {
      return `${this.$t('CALENDAR_ACTIVITY.activityCharts.hrActivityChart.vAxisTitle')} ${this.hrChartUnit === this.hrUnits.BPM ? '(BPM)' : '(%)'}`
    },
    yTicks () {
      return this.hrChartUnit === this.hrUnits.BPM ? [40, 60, 80, 100, 120, 140, 160, 180, 200, 220, 240, 260] : [30, 40, 50, 60, 70, 80, 90, 100, 110];
    }
  },
  methods: {
    onBrushCreation ({ selection }) {
      this.brushCreatingTriggered(selection)
    },
    getMorePreciseHeartRateData ({ selection, chartMetricsType }) {
      if(this.activityInfo.status === this.activityStatus.PROCESSED) {
        if(!selection && !selection.length) {
          return;
        }

        const selectionScope = selection[1] - selection[0];

        if(selectionScope <= (5 * 60)) {
          let startOffsetSeconds = selection[0];
          let endOffsetSeconds = selection[1];

          this.fetchMorePreciseActivityData({
            startOffsetSeconds,
            endOffsetSeconds,
            timeInterval: 2,
            chartMetricsType,
            activityId: this.activityId
          });
        } else if (selectionScope <= (15 * 60)) {
          let startOffsetSeconds = selection[0];
          let endOffsetSeconds = selection[1];

          this.fetchMorePreciseActivityData({
            startOffsetSeconds,
            endOffsetSeconds,
            timeInterval: 10,
            chartMetricsType,
            activityId: this.activityId
          });
        } else if (selectionScope <= (30 * 60)) {
          let startOffsetSeconds = selection[0];
          let endOffsetSeconds = selection[1];

          this.fetchMorePreciseActivityData({
            startOffsetSeconds,
            endOffsetSeconds,
            timeInterval: 30,
            chartMetricsType,
            activityId: this.activityId
          });
        }


        // if(selectionScope <= (15 * 60)) {
        //   let startOffsetSeconds = selection[0];
        //   let endOffsetSeconds = selection[1];

        //   this.fetchMorePreciseActivityData({
        //     startOffsetSeconds,
        //     endOffsetSeconds,
        //     timeInterval: 2,
        //     chartMetricsType,
        //     activityId: this.activityId
        //   });
        // } else if(selectionScope <= (30 * 60) && selectionScope > (15 * 60)) { 
        //   let startOffsetSeconds = selection[0];
        //   let endOffsetSeconds = selection[1];

        //   this.fetchMorePreciseActivityData({
        //     startOffsetSeconds,
        //     endOffsetSeconds,
        //     timeInterval: 30,
        //     chartMetricsType,
        //     activityId: this.activityId
        //   });
        // }
      }
    },

  },
  mixins: [
    ChartMixin
  ],
  components: {
    LineChart
  }
}
</script>
